import React, {ReactNode, useMemo} from 'react';
import styled, {css} from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  DropdownBoxCoordinator,
  DropdownBoxCoordinatorProps,
} from '../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useConstant} from '../../../../../srcCommon/helpers/hookHelpers';
import {MultipleGroupsDropdown} from './multipleGroupsDropdown';
import {DropdownGroup} from './multipleGroupsDropdownHelpers';
import {MultipleGroupsDropdownValue} from './multipleGroupsDropdownValue';

/*
 * Props.
 */

export interface MultipleGroupsDropdownBoxBaseProps<T> extends Partial<DropdownProps> {
  groups: ReadonlyArray<DropdownGroup<any>>;
  selectedItems: ReadonlyArray<T> | null;
  initialSelectedItems?: ReadonlyArray<T> | null;
  placeholder?: ReactNode;
  width?: number;
  buttonWidth?: number | string;
  selectedItemsSeparator?: ReactNode;
  isDisabled?: boolean;
  isSearchDisabled?: boolean;
  sortSelectedItemsToTop?: boolean;
  maxItemsToRender?: number;
  renderButton?: (isOpen: boolean, props: DropdownBoxCoordinatorProps) => ReactNode;
  onChange: HandlerOf<ReadonlyArray<T>>;
  onSearch?: HandlerOf<string>;
  onToggle?: HandlerOf<boolean>;
  onSelectCreateExpression?: Handler;
  /*
   * Will merge all groups into one when you search, and remove group titles.
   * This brings better relevance to the search, but all your groups need to be compatible with same label renderer.
   */
  shouldMergeGroupsWhenSearch?: boolean;
}

export interface MultipleGroupsDropdownBoxWithoutAnyHeaderProps<T> {
  canSelectMultipleItems?: boolean;
  canSelectAnyItem?: never;
  anyLabel?: never;
  anyIcon?: never;
}

export interface MultipleGroupsDropdownBoxWithAnyHeaderProps<T> {
  canSelectMultipleItems: true;
  canSelectAnyItem: true;
  anyLabel: ReactNode;
  anyIcon?: ReactNode;
}

export type MultipleGroupsDropdownBoxProps<T> = MultipleGroupsDropdownBoxBaseProps<T> &
  (MultipleGroupsDropdownBoxWithoutAnyHeaderProps<T> | MultipleGroupsDropdownBoxWithAnyHeaderProps<T>);

/*
 * Styles.
 */

const Wrap = styled.div<{$width?: number | string}>`
  ${(p) => {
    if (!p.$width) {
      return '';
    }
    if (typeof p.$width === 'number') {
      return css`
        width: ${p.$width}px;
      `;
    }
    return css`
      width: ${p.$width};
    `;
  }};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.normal};
`;

const StyledDropdownBoxCoordinator = styled(DropdownBoxCoordinator)`
  padding: 0;
`;

/*
 * Component.
 */

export const MultipleGroupsDropdownBox = <T,>(props: MultipleGroupsDropdownBoxProps<T>) => {
  const isDisabled = props.groups.length === 0 || props.isDisabled;
  const internalInitialSelectedItems = useConstant(() => props.selectedItems);
  const initialSelectedItems = props.initialSelectedItems ?? internalInitialSelectedItems;

  const sortedGroups = useMemo(
    () =>
      props.sortSelectedItemsToTop ? sortInitiallyGroups(props.groups, initialSelectedItems) : props.groups,
    [props.sortSelectedItemsToTop, props.groups, initialSelectedItems],
  );

  return (
    <Wrap $width={props.buttonWidth}>
      <StyledDropdownBoxCoordinator
        noInputMarginRight
        isDisabled={isDisabled}
        value={<MultipleGroupsDropdownValue {...props} groups={sortedGroups} />}
        onPopoverToggle={props.onToggle}
        renderButton={props.renderButton}
        renderDropdown={(popoverProps) => (
          <MultipleGroupsDropdown {...props} groups={sortedGroups} popoverProps={popoverProps} />
        )}
      />
    </Wrap>
  );
};

/*
 * Helpers.
 */

function sortInitiallyGroups<T>(
  groups: ReadonlyArray<DropdownGroup<any>>,
  selectedItems: ReadonlyArray<T> | null,
) {
  if (!selectedItems || selectedItems.length === 0) {
    return groups;
  }

  return groups.map((group) => ({
    ...group,
    items: [...group.items].sort((a: T, b: T) => {
      if (selectedItems.includes(a)) {
        return -1;
      }

      if (selectedItems.includes(b)) {
        return 1;
      }

      return 0;
    }),
  }));
}
